/**
 * PATH: packages/ui/src/kanban/components/settings-home/nav.ts
 * FILE_ID: UI-KANBAN-SETTINGS-NAV-001
 * PURPOSE: Notion-style settings menu: This board vs All Kanban.
 * ROLE IN ARCHITECTURE: Settings-home navigation contract.
 * MAIN EXPORTS: SETTINGS_NAV, SettingsPage, pageMeta, faceScopeFor, drawerScopeFor
 * NON-RESPONSIBILITIES: Does not persist or render.
 * NOTES FOR FUTURE AI: Density default lives on All Kanban Card face, not Board.
 */

export type SettingsPage =
  | 'board-face'
  | 'board-drawer'
  | 'board-general'
  | 'kanban-face'
  | 'kanban-drawer'
  | 'kanban-general'

export type SettingsNavItem = {
  id: SettingsPage
  title: string
  hint: string
}

export type SettingsNavGroup = {
  id: 'board' | 'kanban'
  title: string
  hint: string
  items: SettingsNavItem[]
}

export const SETTINGS_NAV: SettingsNavGroup[] = [
  {
    id: 'board',
    title: 'This board',
    hint: 'Only this board',
    items: [
      { id: 'board-general', title: 'Board', hint: 'Name, lists, and rules' },
      { id: 'board-face', title: 'Card', hint: 'Tile on this board' },
      { id: 'board-drawer', title: 'Drawer', hint: 'Card drawer on this board' },
    ],
  },
  {
    id: 'kanban',
    title: 'All Kanban',
    hint: 'Defaults for every board',
    items: [
      { id: 'kanban-face', title: 'Card', hint: 'Default tile' },
      { id: 'kanban-drawer', title: 'Drawer', hint: 'Default card drawer' },
      { id: 'kanban-general', title: 'Workspace', hint: 'Dock, keyboard, and default boards' },
    ],
  },
]

export function pageMeta(page: SettingsPage): { title: string; hint: string } {
  for (const group of SETTINGS_NAV) {
    const item = group.items.find((entry) => entry.id === page)
    if (item) {
      return {
        title: item.title,
        hint: `${group.title}. ${item.hint}.`,
      }
    }
  }
  return { title: 'Settings', hint: '' }
}

export function faceScopeFor(page: SettingsPage): 'board' | 'all' {
  return page.startsWith('kanban-') ? 'all' : 'board'
}

export function drawerScopeFor(page: SettingsPage): 'board' | 'all' {
  return page.startsWith('kanban-') ? 'all' : 'board'
}
